import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom'; // Added for navigation

const API_URL = 'https://www.414ministry.org/api';

function LogProgress() {
    const [books, setBooks] = useState([]);
    const [selectedBook, setSelectedBook] = useState('');
    const [chapters, setChapters] = useState([]);
    const [selectedChapters, setSelectedChapters] = useState([]);
    const [error, setError] = useState('');
    const [isSaving, setIsSaving] = useState(false); // Added to handle save animation
    const navigate = useNavigate(); // Added for navigation

    useEffect(() => {
        console.log('Fetching books for LogProgress...');
        axios.get(`${API_URL}/books`, { withCredentials: true })
            .then(res => {
                console.log('Books response:', res.data);
                if (!res.data || !Array.isArray(res.data)) {
                    throw new Error('Invalid books data');
                }
                setBooks(res.data);
            })
            .catch(err => {
                console.error('Error fetching books:', err);
                setError('Failed to load books: ' + (err.response?.data?.error || err.message));
            });
    }, []);

    const handleBookChange = (e) => {
        const book = e.target.value;
        console.log('Selected book:', book);
        setSelectedBook(book);
        setSelectedChapters([]);
        setChapters([]);
        setError('');

        if (book) {
            console.log('Fetching chapters for book:', book);
            axios.get(`${API_URL}/chapters/${book}`, { withCredentials: true })
                .then(res => {
                    console.log('Chapters response:', res.data);
                    if (!res.data || typeof res.data.total_chapters !== 'number') {
                        throw new Error('Invalid chapters data');
                    }
                    const chapterCount = res.data.total_chapters;
                    const newChapters = [...Array(chapterCount).keys()].map(i => i + 1);
                    console.log('Setting chapters:', newChapters);
                    setChapters(newChapters);

                    console.log('Fetching existing progress for book:', book);
                    return axios.get(`${API_URL}/progress`, { withCredentials: true });
                })
                .then(res => {
                    console.log('Progress response:', res.data);
                    const progressMap = res.data.books.reduce((acc, bookData) => {
                        if (bookData.book_name === book) {
                            acc = bookData.chapters_read || [];
                        }
                        return acc;
                    }, []);
                    console.log('Setting selected chapters from progress:', progressMap);
                    setSelectedChapters(progressMap);
                })
                .catch(err => {
                    console.error('Error fetching chapters or progress:', err);
                    setError('Failed to load chapters or progress: ' + (err.response?.data?.error || err.message));
                });
        }
    };

    const toggleChapter = (chapter) => {
        console.log('Toggling chapter:', chapter);
        setSelectedChapters(prev =>
            prev.includes(chapter)
                ? prev.filter(ch => ch !== chapter)
                : [...prev, chapter].sort((a, b) => a - b)
        );
    };

    const saveProgress = () => {
        console.log('Saving progress:', { book: selectedBook, chapters: selectedChapters });
        if (!selectedBook || !selectedChapters || selectedChapters.length === 0) {
            console.log('Validation failed on frontend:', { book: selectedBook, chapters: selectedChapters });
            alert('Please select a book and at least one chapter');
            return;
        }

        setIsSaving(true);
        axios.post(`${API_URL}/log-progress`, {
            book: selectedBook,
            chapters: selectedChapters
        }, { withCredentials: true })
            .then(() => {
                alert('Progress saved successfully');
                console.log('Re-fetching progress after save for book:', selectedBook);
                return axios.get(`${API_URL}/progress`, { withCredentials: true });
            })
            .then(res => {
                console.log('Progress response after save:', res.data);
                const progressMap = res.data.books.reduce((acc, bookData) => {
                    if (bookData.book_name === selectedBook) {
                        acc = bookData.chapters_read || [];
                    }
                    return acc;
                }, []);
                console.log('Setting selected chapters from progress after save:', progressMap);
                setSelectedChapters(progressMap);
            })
            .catch(err => {
                console.error('Error saving progress:', err);
                alert('Failed to save progress: ' + (err.response?.data?.error || err.message));
            })
            .finally(() => {
                setIsSaving(false);
            });
    };

    const goBack = () => {
        console.log('Navigating back to dashboard');
        navigate('/dashboard'); // Updated to use navigate instead of window.location.href
    };

    if (error) {
        return (
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <h2 className="text-2xl font-bold text-gray-900 sm:text-3xl mb-4">Log Progress</h2>
                <p className="text-red-600">{error}</p>
            </div>
        );
    }

    if (!books || books.length === 0) {
        return (
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <h2 className="text-2xl font-bold text-gray-900 sm:text-3xl mb-4">Log Progress</h2>
                <p className="text-gray-600">Loading books...</p>
            </div>
        );
    }

    return (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <h2 className="text-2xl font-bold text-gray-900 sm:text-3xl mb-6">Log Progress</h2>
            <div className="mb-6">
                <select
                    className="block w-full max-w-md mx-auto px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-700"
                    value={selectedBook}
                    onChange={handleBookChange}
                >
                    <option value="">Select a Book</option>
                    {books.map(book => (
                        <option key={book.book_id} value={book.book_name}>{book.book_name}</option>
                    ))}
                </select>
            </div>
            {selectedBook ? (
                chapters.length > 0 ? (
                    <div className="mb-6">
                        <h3 className="text-xl font-semibold text-gray-900 text-center mb-4">{selectedBook}</h3>
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 lg:grid-cols-6 gap-3 max-w-4xl mx-auto">
                            {chapters.map(chapter => (
                                <button
                                    key={chapter}
                                    className={`px-4 py-2 rounded-md text-sm font-medium transition-colors duration-200 ${
                                        selectedChapters.includes(chapter)
                                            ? 'bg-blue-600 text-white hover:bg-blue-700'
                                            : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                    }`}
                                    onClick={() => toggleChapter(chapter)}
                                >
                                    {chapter}
                                </button>
                            ))}
                        </div>
                        <p className="mt-4 text-center text-gray-600">
                            Selected <span className="font-semibold text-blue-600">{selectedChapters.length}</span> of {chapters.length} chapters
                        </p>
                    </div>
                ) : (
                    <p className="text-center text-gray-600 mb-6">
                        Loading chapters... 
                        <span className="inline-block ml-2 animate-spin rounded-full h-5 w-5 border-t-2 border-blue-600"></span>
                    </p>
                )
            ) : null}
            <div className="flex justify-center space-x-4">
                <button
                    className={`px-6 py-2 rounded-md text-sm font-medium text-white transition-colors duration-200 ${
                        !selectedBook || selectedChapters.length === 0 || isSaving
                            ? 'bg-gray-400 cursor-not-allowed'
                            : 'bg-blue-600 hover:bg-blue-700'
                    } ${isSaving ? 'animate-pulse' : ''}`}
                    onClick={saveProgress}
                    disabled={!selectedBook || selectedChapters.length === 0 || isSaving}
                >
                    {isSaving ? 'Saving...' : 'Save Progress'}
                </button>
                <button
                    className="px-6 py-2 rounded-md text-sm font-medium text-gray-700 bg-gray-200 hover:bg-gray-300 transition-colors duration-200"
                    onClick={goBack}
                >
                    Back to Dashboard
                </button>
            </div>
        </div>
    );
}

export default LogProgress;